import { useEffect, useRef, type JSX } from 'react';
import { useStudio, useUi } from '../../context';
import { selectTheme } from '../../uiStore';
import { createMeterState, DANGER_DB, dbToMeter, isIdle, stepMeter, WARN_DB, type MeterState } from './ballistics';
import { meterTicker } from './meterLoop';

export interface MeterColors {
  background: string;
  green: string;
  yellow: string;
  red: string;
  greenDim: string;
  yellowDim: string;
  redDim: string;
  hold: string;
  clip: string;
}

/** The dark palette; also the fallback for any token the stylesheet does not define. */
export const DEFAULT_METER_COLORS: MeterColors = {
  background: '#05070a',
  green: '#35c46f',
  yellow: '#e6c229',
  red: '#ee4b4b',
  greenDim: '#143a24',
  yellowDim: '#3f3610',
  redDim: '#451a1a',
  hold: '#ffffff',
  clip: '#ff2b2b',
};

const METER_TOKENS: Record<keyof MeterColors, string> = {
  background: '--ui-meter-bg',
  green: '--ui-meter-green',
  yellow: '--ui-meter-yellow',
  red: '--ui-meter-red',
  greenDim: '--ui-meter-green-dim',
  yellowDim: '--ui-meter-yellow-dim',
  redDim: '--ui-meter-red-dim',
  hold: '--ui-meter-hold',
  clip: '--ui-meter-clip',
};

/** The meter palette from the `--ui-meter-*` tokens in effect at `element` (they change with the theme). */
export function readMeterColors(element: Element): MeterColors {
  const style = getComputedStyle(element);
  const out = { ...DEFAULT_METER_COLORS };
  for (const key of Object.keys(METER_TOKENS) as (keyof MeterColors)[]) {
    const value = style.getPropertyValue(METER_TOKENS[key]).trim();
    if (value) out[key] = value;
  }
  return out;
}

const CLIP_HEIGHT = 5;

/** Paints one meter frame. Exported for tests that inspect draw calls. */
export function drawMeter(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  state: MeterState,
  COLORS: MeterColors = DEFAULT_METER_COLORS,
): void {
  ctx.fillStyle = COLORS.background;
  ctx.fillRect(0, 0, width, height);
  const channels = Math.max(1, state.channels.length);
  const gap = channels > 1 ? Math.max(1, Math.round(width * 0.08)) : 0;
  const barWidth = (width - gap * (channels - 1)) / channels;
  const top = CLIP_HEIGHT + 2;
  const span = height - top;
  const y = (db: number): number => top + (1 - dbToMeter(db)) * span;
  const zones: readonly [number, number, string, string][] = [
    [-Infinity, WARN_DB, COLORS.green, COLORS.greenDim],
    [WARN_DB, DANGER_DB, COLORS.yellow, COLORS.yellowDim],
    [DANGER_DB, 0, COLORS.red, COLORS.redDim],
  ];

  for (let c = 0; c < channels; c++) {
    const x = c * (barWidth + gap);
    const channel = state.channels[c];
    const peakY = channel ? y(channel.peak) : height;
    const rmsY = channel ? y(channel.rms) : height;
    for (const [from, to, lit, dim] of zones) {
      const zoneBottom = from === -Infinity ? height : y(from);
      const zoneTop = y(to);
      ctx.fillStyle = dim;
      ctx.fillRect(x, zoneTop, barWidth, zoneBottom - zoneTop);
      // Peak: a translucent full-width bar; RMS: a solid narrower bar on top of it.
      const peakTop = Math.max(zoneTop, peakY);
      if (peakTop < zoneBottom) {
        ctx.globalAlpha = 0.55;
        ctx.fillStyle = lit;
        ctx.fillRect(x, peakTop, barWidth, zoneBottom - peakTop);
        ctx.globalAlpha = 1;
      }
      const rmsTop = Math.max(zoneTop, rmsY);
      if (rmsTop < zoneBottom) {
        ctx.fillStyle = lit;
        ctx.fillRect(x + barWidth * 0.2, rmsTop, barWidth * 0.6, zoneBottom - rmsTop);
      }
    }
    if (channel && dbToMeter(channel.hold) > 0) {
      ctx.fillStyle = COLORS.hold;
      ctx.fillRect(x, Math.max(top, y(channel.hold) - 1), barWidth, 2);
    }
  }
  ctx.fillStyle = state.clipAge !== null ? COLORS.clip : COLORS.redDim;
  ctx.fillRect(0, 0, width, CLIP_HEIGHT);
}

/** Canvas level meter for one strip (a source id or MASTER_STRIP). */
export function Meter({ stripId, label }: { stripId: string; label: string }): JSX.Element {
  const studio = useStudio();
  const canvas = useRef<HTMLCanvasElement>(null);
  const theme = useUi(selectTheme);
  const colors = useRef<MeterColors>(DEFAULT_METER_COLORS);
  /** Set when the palette changed, so an idle meter repaints once. */
  const repaint = useRef(true);

  // Re-read the palette whenever the theme changes; data-theme is already on <html> by then.
  useEffect(() => {
    if (canvas.current) colors.current = readMeterColors(canvas.current);
    repaint.current = true;
  }, [theme]);

  useEffect(() => {
    const node = canvas.current;
    if (!node) return;
    const ctx = node.getContext('2d');
    if (!ctx) return;
    const state = createMeterState();
    let dirty = true;

    const resize = (): void => {
      const rect = node.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const width = Math.max(1, Math.round(rect.width * dpr));
      const height = Math.max(1, Math.round(rect.height * dpr));
      if (node.width !== width || node.height !== height) {
        node.width = width;
        node.height = height;
        dirty = true;
      }
    };
    resize();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(resize);
    observer?.observe(node);

    const unsubscribe = meterTicker(studio).subscribe((levels, dt) => {
      stepMeter(state, levels[stripId], dt);
      const idle = isIdle(state);
      if (repaint.current) {
        repaint.current = false;
        dirty = true;
      }
      if (idle && !dirty) return;
      drawMeter(ctx, node.width, node.height, state, colors.current);
      dirty = !idle;
    });
    return () => {
      unsubscribe();
      observer?.disconnect();
    };
  }, [studio, stripId]);

  return <canvas ref={canvas} className="strip-meter" role="img" aria-label={label} />;
}
